import { Component, ComponentFactoryResolver, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
	BroadcastService,
	GridBaseComponent,
	SubContentService,
	ToasterType,
	SetIsHasPermissions,
} from '@cv-invision/core';
import { CvDateFormatter } from '@cv-invision/cv.component-library.ux';
import { Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NexsysMonitorService } from '../../service/nexsys-monitor.service';
import { ORIGIN, URLS } from '../model/nexsys-monitor.constant';
import { NexsysOrderLogsColumnsModel } from '../model/nexsys-order-logs-columns.model';
import { SetNexsysOrderLogInfo } from '../state/actions/nexsys-monitor.actions';
import { CustomActionRetryCellComponent } from './custom-action-grid-cell/custom-action-grid-cell.component';
import { LogsOrderDescriptionComponent } from './logs-description/logs-description.component';

@Component({
	selector: 'cv-nexsys-logs-order',
	templateUrl: './nexsys-logs-order.component.html',
	styleUrls: ['./nexsys-logs-order.component.scss']
})
export class NexsysLogsOrderComponent extends GridBaseComponent implements OnInit, OnDestroy {
	private atComponentDestroy: Subject<any> = new Subject<any>();
	orderId: string;
	externalId: string;
	nexsysOrderBaseData: any;
	rowClassRules: any;
	origin: string;

	constructor(
		private nexsysMonitorService: NexsysMonitorService,
		private activateRoute: ActivatedRoute,
		private componentFactoryResolver: ComponentFactoryResolver,
		private subContentService: SubContentService,
		private broadcastService: BroadcastService,
		private store: Store,
		private router: Router,
		private dateService: CvDateFormatter
	) {
		super();

		this.rowClassRules = {
			'error-row': function(params: any): boolean {
				return !params.data.valid;
			}
		};
	}

	ngOnInit(): void {
		if (this.router.url.includes(URLS.NEXSYS)) {
			this.origin = ORIGIN.Nexsys;
			this.orderId = this.activateRoute.snapshot.paramMap.get('id');
		} else if (this.router.url.includes(URLS.NEXSYS_ORPHAN)) {
			this.origin = ORIGIN.NexsysLegacy;
			this.orderId = this.activateRoute.snapshot.paramMap.get('id');
		} else if (this.router.url.includes(URLS.VALUTRUST)) {
			this.orderId = this.activateRoute.snapshot.paramMap.get('id');
			this.origin = ORIGIN.Valutrust;
		} else if (this.router.url.includes(URLS.XPANSE)) {
			this.origin = ORIGIN.Xpanse;
			this.orderId = this.activateRoute.snapshot.paramMap.get('transactionId');
			this.externalId = this.activateRoute.snapshot.paramMap.get('SPNId');
		} else if (this.router.url.includes(URLS.ORDER_EXTERNAL)){
			this.origin = ORIGIN.OrderExternal;
			this.orderId = this.activateRoute.snapshot.paramMap.get('id');
		} else if (this.router.url.includes(URLS.FNC)){
			this.origin = ORIGIN.FNC;
			this.orderId = this.activateRoute.snapshot.paramMap.get('id');
		} else if (this.router.url.includes(URLS.EVO)){
			this.origin = ORIGIN.EVO;
			this.orderId = this.activateRoute.snapshot.paramMap.get('id');
		} else if (this.router.url.includes(URLS.VALUE_LINK)) {
			this.origin = ORIGIN.ValueLink;
			this.orderId = this.activateRoute.snapshot.paramMap.get('id');
		} else if (this.router.url.includes(URLS.CONNEXIONS)) {
			this.origin = ORIGIN.Connexions;
			this.orderId = this.activateRoute.snapshot.paramMap.get('id');
		} else if (this.router.url.includes(URLS.LENDINGQB)) {
			this.origin = ORIGIN.Lendingqb;
			this.orderId = this.activateRoute.snapshot.paramMap.get('id');
		}
		this.gridOptions.columnDefs = new NexsysOrderLogsColumnsModel(this.dateService).columns;
		this.gridOptions.frameworkComponents = {
			customActionRetryCellComponent: CustomActionRetryCellComponent
		};
		this.loadGridData();
		// subscribe to the reload list event.
		this.nexsysMonitorService
			.getReloadListListener()
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((reload: boolean) => {
				if (reload) {
					this.loadGridData();
				}
			});
	}
	/**
	 * Selected order
	 */
	public onSelectionChanged(): void {
		const selectedRows = this.gridApi.getSelectedRows();
		const selectedRow = selectedRows[0];
		if (selectedRow) {
			this.openSubComponent(selectedRow);
		} else {
			this.subContentService.close();
		}
	}
	/**
	 * Open the component with the Selected Row
	 * @param selectedRow
	 */
	private openSubComponent(selectedRow: any = null): void {
		const source = this.origin;
		this.subContentService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: LogsOrderDescriptionComponent,
				data: { trans: selectedRow, ...this.nexsysOrderBaseData, source }
			})
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe();
	}

	/**
	 * open nexsys log order
	 */
	loadGridData(): void {
		this.nexsysMonitorService
			.getNexsysOrderTransLog(this.orderId, this.origin, this.externalId)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(
				result => {
					this.nexsysOrderBaseData = Object.assign({}, result);
					delete this.nexsysOrderBaseData['transactionLogs'];
					this.store.dispatch(new SetNexsysOrderLogInfo(this.nexsysOrderBaseData));
					this.gridData = result.transactionLogs;
				},
				error => {
					this.errorMsgFunction(error.error.error);
				}
			);
	}

	/**
	 * Display error message weather the operation wast´n success
	 * @param error
	 */
	errorMsgFunction(error: string): void {
		const event = {
			error: error,
			autoClose: true
		};
		this.broadcastService.broadcast({
			name: ToasterType.ERROR,
			event: event
		});

		this.store.dispatch(new SetIsHasPermissions(null));
	}

	/**
	 * On component destroy
	 */
	ngOnDestroy(): void {
		this.subContentService.close();
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
